import React, { useCallback, useEffect, useState } from 'react';
import { Key } from 'ts-key-enum';

import { IngestionArtifactModel, IngestionArtifactType, IngestionResourceModel } from '../../../../../../gamma-api';
import IbSpin from '../../../../../components/common/IbSpin';
import ResizableDrawer from '../../../../../components/common/ResizableDrawer';
import TextChunk from '../../../../../components/TextChunk';
import ArtifactListTitle from '../ArtifactListTitle';

import './index.less';

import { MAIN_CLASS_NAME } from './const';

export enum ContentRenderMode {
  MARKDOWN = 'markdown',
  PLAINTEXT = 'plaintext',
  BOTH = 'both',
}

export interface IFragmentListProps {
  visible: boolean;
  loading: boolean;
  resource?: IngestionResourceModel;
  items: IngestionArtifactModel[];
  onOpen?: (
    resource: IngestionResourceModel,
    artifactType: IngestionArtifactType,
    oldArtifactType?: IngestionArtifactType
  ) => void;
  onClose?: (artifactType: IngestionArtifactType) => void;
  onResourceNext?: (currentResource: IngestionResourceModel, artifactType: IngestionArtifactType) => void;
  onResourcePrevious?: (currentResource: IngestionResourceModel, artifactType: IngestionArtifactType) => void;
}

const FragmentList: React.FC<IFragmentListProps> = ({
  visible,
  loading,
  resource,
  items,
  onOpen,
  onClose,
  onResourceNext,
  onResourcePrevious,
}) => {
  const [renderMode, setRenderMode] = useState(ContentRenderMode.MARKDOWN);

  const onNextResourceClick = useCallback(() => {
    resource && onResourceNext?.(resource, IngestionArtifactType.Fragment);
  }, [resource, onResourceNext]);

  const onPreviousResourceClick = useCallback(() => {
    resource && onResourcePrevious?.(resource, IngestionArtifactType.Fragment);
  }, [resource, onResourcePrevious]);

  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === Key.ArrowRight) {
        e.preventDefault();
        onNextResourceClick();
      } else if (e.key === Key.ArrowLeft) {
        e.preventDefault();
        onPreviousResourceClick();
      } else if (e.key.toLowerCase() === 'c' && !e.ctrlKey) {
        e.preventDefault();
        resource && onOpen?.(resource, IngestionArtifactType.Article, IngestionArtifactType.Fragment);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, onNextResourceClick, onPreviousResourceClick, onOpen]);

  const renderTitle = () => {
    const onRenderModeChange = (value: string) => setRenderMode(value as ContentRenderMode);

    return (
      <ArtifactListTitle
        artifactType={IngestionArtifactType.Fragment}
        renderMode={renderMode}
        resource={resource}
        onNextResourceClick={onNextResourceClick}
        onOpen={onOpen}
        onPreviousResourceClick={onPreviousResourceClick}
        onRenderModeChange={onRenderModeChange}
      />
    );
  };

  const renderItem = (item: IngestionArtifactModel, index: number) => {
    return (
      <TextChunk
        key={item.artifact_id}
        chunk_num={index + 1}
        contentId={item.resource_id}
        metadata={item.metadata}
        renderMode={renderMode}
        text={item.content.data}
        volume={item.volumes.latest}
      />
    );
  };

  const renderContent = () => {
    if (loading) {
      return <IbSpin />;
    }

    return items.map(renderItem);
  };

  const onDrawerClose = () => {
    onClose?.(IngestionArtifactType.Fragment);
  };

  return (
    <ResizableDrawer
      resizable
      className={MAIN_CLASS_NAME}
      title={renderTitle()}
      visible={visible}
      width="75%"
      onClose={onDrawerClose}
    >
      {renderContent()}
    </ResizableDrawer>
  );
};

export default FragmentList;
